import { Box, CircularProgress, Typography } from "@mui/material";
import { isNil } from "lodash";

import { ErrorTypography } from "components/ErrorTypography";
import {
  useCarrierStats,
  useCompanyProfile,
  useCompanyReviews,
  useDisclosure,
} from "hooks";
import { PageLayout } from "layouts";

import { CarrierAbout } from "./components/CarrierAbout";
import { CarrierEmergencyContact } from "./components/CarrierEmergencyContact";
import { CarrierInformation } from "./components/CarrierInformation";
import { CarrierProfileCompletionAlert } from "./components/CarrierProfileCompletionAlert";
import { CarrierProfileEditModal } from "./components/CarrierProfileEditModal";
import { CarrierStatsCards } from "./components/CarrierStatsCards";
import { CarrierTeam } from "./components/CarrierTeam";
import { CompanyProfileFooter } from "./components/CompanyProfileFooter";
import { CompanyProfileHeader } from "./components/CompanyProfileHeader";
import { CompanyProfileReviews } from "./components/CompanyProfileReviews";

interface CenteredPageLayoutProps {
  children: React.ReactNode;
}

function CenteredPageLayout(props: CenteredPageLayoutProps) {
  const { children } = props;

  return (
    <PageLayout>
      <Box display="flex" alignItems="center" justifyContent="center">
        {children}
      </Box>
    </PageLayout>
  );
}

interface Props {
  companyId: number;
}

export function CompanyProfilePageLayout(props: Props) {
  const { companyId } = props;

  const { companyProfile, loading, error, refetchProfile } =
    useCompanyProfile(companyId);

  const {
    error: reviewsError,
    loading: loadingReviews,
    reviewProfile,
    refetchReviews,
  } = useCompanyReviews({ companyId });

  // Same request the stats cards and reviews header make; SWR dedupes it.
  const { loading: loadingStats } = useCarrierStats({ companyId });

  const {
    isOpen: isEditModalOpen,
    onOpen: onOpenEditModal,
    onClose: onCloseEditModal,
  } = useDisclosure();

  if (!isNil(error)) {
    return (
      <CenteredPageLayout>
        <ErrorTypography error={error} />
      </CenteredPageLayout>
    );
  }

  if (loading) {
    return (
      <CenteredPageLayout>
        <CircularProgress />
      </CenteredPageLayout>
    );
  }

  if (isNil(companyProfile)) {
    return (
      <CenteredPageLayout>
        <Typography>
          There is no company profile information available
        </Typography>
      </CenteredPageLayout>
    );
  }

  return (
    <PageLayout>
      <CarrierProfileCompletionAlert
        companyProfile={companyProfile}
        onEditProfile={onOpenEditModal}
      />
      <CompanyProfileHeader
        companyProfile={companyProfile}
        onLogoChanged={refetchProfile}
        onEditProfile={onOpenEditModal}
      />
      <CarrierAbout companyProfile={companyProfile} />
      <CarrierStatsCards companyId={companyId} />
      <CarrierInformation
        businessType={companyProfile.company.business_type}
        profileAbout={companyProfile.profile_about}
      />
      <CarrierTeam
        companyId={companyId}
        companyName={companyProfile.company.name}
      />
      <CompanyProfileReviews
        companyProfile={companyProfile}
        reviewsError={reviewsError}
        loadingReviews={loadingReviews}
        reviewProfile={reviewProfile}
        refetchReviews={refetchReviews}
      />
      <CarrierEmergencyContact
        profileAbout={companyProfile.profile_about}
        isScrollEnabled={!loadingStats && !loadingReviews}
      />
      <CompanyProfileFooter
        companyName={companyProfile.company.name}
        profileAbout={companyProfile.profile_about}
      />
      <CarrierProfileEditModal
        companyProfile={companyProfile}
        modalOpen={isEditModalOpen}
        onModalClose={onCloseEditModal}
        onEditSuccess={refetchProfile}
      />
    </PageLayout>
  );
}
